import Axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import {
  ACCESO360_API_URL,
  ACCESO360_LEGACY_API_URL,
  MEDIA_MANAGER_API_URL,
  SEARCH_API_URL,
} from '@/config';
import { LANGUAGE_KEY } from '@/constants';

import keycloak from './keycloak';

const MIN_TOKEN_VALIDITY_SECONDS = 60;
let refreshPromise: Promise<boolean> | null = null;

async function ensureFreshToken(minValidity: number = MIN_TOKEN_VALIDITY_SECONDS): Promise<void> {
  if (!keycloak.token) return;

  if (refreshPromise) {
    try {
      await refreshPromise;
    } finally {
    }
    return;
  }

  refreshPromise = keycloak.updateToken(minValidity);
  try {
    await refreshPromise;
  } catch (e) {
    try {
      keycloak.clearToken();
    } catch {}
    throw e;
  } finally {
    refreshPromise = null;
  }
}

async function authRequestInterceptor(
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  config.headers = config.headers || {};

  const isRelativeRequest = !config.url?.startsWith('http');
  if (isRelativeRequest) {
    await ensureFreshToken();
    if (keycloak.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
  }

  config.headers.Accept = 'application/json';
  return config;
}

function languageInterceptor(config: InternalAxiosRequestConfig) {
  const language = localStorage.getItem(LANGUAGE_KEY);

  if (language) {
    config.headers['Accept-Language'] = language.split('_')[0] ?? 'es';
  }

  return config;
}

function onFulfilled(response: AxiosResponse) {
  return response.data;
}

function onRejected(error: any) {
  return Promise.reject(error);
}

function baseFactory(baseURL: string) {
  return Axios.create({ baseURL });
}

function factory(baseURL: string) {
  const client = baseFactory(baseURL);
  client.interceptors.request.use(authRequestInterceptor);
  client.interceptors.request.use(languageInterceptor);
  client.interceptors.response.use(onFulfilled, async error => {
    const status = error?.response?.status;
    const originalConfig = error?.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    if (status === 401 && originalConfig && !originalConfig._retry) {
      originalConfig._retry = true;
      try {
        await ensureFreshToken();
        if (keycloak.token) {
          originalConfig.headers = originalConfig.headers || {};
          originalConfig.headers.Authorization = `Bearer ${keycloak.token}`;
        }
        return client.request(originalConfig);
      } catch {}
    }

    return onRejected(error);
  });
  return client;
}

export const acceso360Client = factory(ACCESO360_API_URL);
export const acceso360LegacyClient = factory(ACCESO360_LEGACY_API_URL);
export const searchClient = factory(SEARCH_API_URL);
export const mediaManagerClient = factory(MEDIA_MANAGER_API_URL);
export const publicClient = (baseURL: string) => baseFactory(baseURL);
